/**
 * Click Tracking Service
 *
 * Tracks user clicks on elements with data-track-click attribute
 * Clicks are batched and sent with page exit events to minimize HTTP requests
 * Includes rage click detection to identify user frustration
 */

import { trackEvent } from "logger/logger";
import { pageTrackingService, TRACKING_VERSION } from "logger/page-tracking";

/**
 * Static click tracking IDs
 * These are the known, fixed click IDs used throughout the application
 */
type StaticClickId =
    | "finalize-booking"
    | "summary-edit-activity"
    | "summary-remove-activity"
    | "edit-booking-back"
    | "cancel-edit"
    | "mybooking-edit-booking"
    | "mybooking-cancel-booking"
    | "mybooking-book-more"
    | "booking-request-continue"
    | "preorder-item-add-iconbutton"
    | "preorder-item-minus-quantitybutton"
    | "preorder-item-plus-quantitybutton"
    | "preorder-item-radio"
    | "preorder-item-checkbox"
    | "hamburger-button"
    | "exit-booking"
    | "error-reservation-failed-select-date"
    | "error-something-wrong-try-again"
    | "error-server-not-respond-try-again"
    | "error-payment-start-over"
    | "stepper-back"
    | "select-guest-overflow"
    | "show-more-text"
    | "show-less-text"
    | "edit-booking-time"
    | "edit-booking-guests"
    | "edit-booking-contact"
    | "edit-booking-message"
    | "continue-select-activity-time"
    | "continue-select-activity-preorder"
    | "sidenav-language"
    | "sidenav-privacypolicy";

/**
 * Dynamic click IDs with finite options
 */
type SaveEditOption = "time" | "guests" | "contact" | "message";
type SaveEditClickId = `save-edit-${SaveEditOption}`;

/**
 * Known click IDs - combination of static and finite dynamic patterns
 */
type KnownClickId = StaticClickId | SaveEditClickId;

/**
 * Click tracking ID type
 * Accepts known IDs (with autocomplete) or any string for dynamic patterns
 */
type ClickId = KnownClickId | (string & {});

/**
 * Helper builders for dynamic click ID patterns
 * Use these for runtime-generated IDs to maintain consistency
 *
 * @example
 * ClickIdBuilders.selectGuest(2) // "select-guest-2"
 * ClickIdBuilders.selectActivity("Pizza Making") // "select-activity-pizzamaking"
 */
export const ClickIdBuilders = {
    /** Select guest count button: select-guest-{count} */
    selectGuest: (count: number): string => `select-guest-${count}`,
    /** Select children count button: select-children-{count} */
    selectChildren: (count: number): string => `select-children-${count}`,
    /** Select activity button: select-activity-{sanitizedName} */
    selectActivity: (activityName: string): string =>
        `select-activity-${sanitizeTrackingId(activityName)}`,
    /** Select time slot button: select-time-{sanitizedRule}-{HHmm} */
    selectTime: (ruleTitle: string, timeHHmm: string): string =>
        `select-time-${sanitizeTrackingId(ruleTitle)}-${timeHHmm}`,
    /** Preorder tab: preorder-tab-{index} */
    preorderTab: (index: number): string => `preorder-tab-${index}`,
};

/**
 * Sanitizes text for tracking IDs
 * Removes special characters (including Nordic/Germanic characters), spaces, and converts to lowercase
 * Only keeps a-z characters
 */
export function sanitizeTrackingId(text: string): string {
    return text
        .toLowerCase()
        .normalize("NFD") // Decompose characters like å -> a + ˚
        .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
        .replace(/[^a-z]/g, "") // Keep only a-z
        .slice(0, 50); // Limit length to avoid overly long tracking IDs
}

/**
 * Tracked click event data
 */
interface ClickEvent {
    /** Unique identifier from data-track-click attribute */
    id: string;
    /** Element text content (truncated) */
    text: string;
    /** Element type (button, a) */
    elementType: string;
    /** Milliseconds since page view started */
    timeFromPageStart: number;
    /** ISO timestamp when click occurred */
    timestamp: string;
}

/**
 * Click data to be included in page exit events
 */
export interface ClickData {
    clicks: ClickEvent[];
    clickCount: number;
}

/**
 * Elements that can be tracked
 */
const TRACKABLE_ELEMENTS = ["BUTTON", "A"];

/**
 * Maximum length of element text to capture
 */
const MAX_TEXT_LENGTH = 60;

/**
 * Debounce window in milliseconds
 * Prevents tracking the same element multiple times within this window
 */
const DEBOUNCE_MS = 400;

/**
 * Rage click detection configuration
 */
const RAGE_CLICK_THRESHOLD = 3; // Number of clicks to trigger rage detection
const RAGE_CLICK_WINDOW_MS = 1000; // Time window for detecting rapid clicks (1 second)

/**
 * Recent click for rage detection
 */
interface RecentClick {
    id: string;
    time: number;
    text: string;
    elementType: string;
}

class ClickTrackingService {
    private clicks: ClickEvent[] = [];
    private pageStartTime: number = Date.now();
    private isInitialized: boolean = false;
    private boundHandleClick: ((event: MouseEvent) => void) | null = null;
    private lastClickTimes: Map<string, number> = new Map();
    private recentClicks: RecentClick[] = [];
    private rageClicksDetected: Set<string> = new Set(); // Track already reported rage clicks
    private rageClickBuffer: Map<string, number[]> = new Map();

    /**
     * Handle click event from global listener
     */
    handleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const trackableElement = this.findTrackableElement(target);
        if (!trackableElement) return;

        const trackId = trackableElement.getAttribute("data-track-click");
        if (!trackId) return;

        const now = Date.now();
        const elementText = this.getElementText(trackableElement);
        const elementType = trackableElement.tagName.toLowerCase();

        // Detect rage clicks WITHOUT debouncing (all clicks count)
        this.detectRageClickUnbounced(trackId, elementText, elementType, now);

        // Apply debouncing for regular click recording
        const lastClickTime = this.lastClickTimes.get(trackId);

        if (lastClickTime && now - lastClickTime < DEBOUNCE_MS) {
            return;
        }

        this.lastClickTimes.set(trackId, now);

        // Record the click
        this.clicks.push({
            id: trackId,
            text: elementText,
            elementType: elementType,
            timeFromPageStart: now - this.pageStartTime,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Detect rage clicks WITHOUT debouncing (counts all rapid clicks)
     * Uses a separate buffer to track all clicks, not affected by debouncing
     * @param clickId - The click ID
     * @param text - Element text
     * @param elementType - Element type
     * @param now - Current timestamp
     */
    private detectRageClickUnbounced(
        clickId: string,
        text: string,
        elementType: string,
        now: number
    ): void {
        // Initialize buffer for this click ID if needed
        if (!this.rageClickBuffer.has(clickId)) {
            this.rageClickBuffer.set(clickId, []);
        }

        const clickTimes = this.rageClickBuffer.get(clickId)!;
        clickTimes.push(now);

        // Keep only clicks within the rage detection window
        const validClicks = clickTimes.filter((time) => now - time < RAGE_CLICK_WINDOW_MS);
        this.rageClickBuffer.set(clickId, validClicks);

        // Trigger rage click detection if threshold is met
        if (validClicks.length >= RAGE_CLICK_THRESHOLD) {
            // Only report once per time window to avoid spam
            const rageKey = `${clickId}-${Math.floor(now / RAGE_CLICK_WINDOW_MS)}`;
            if (!this.rageClicksDetected.has(rageKey)) {
                this.rageClicksDetected.add(rageKey);
                this.reportRageClick(clickId, text, elementType, validClicks.length);
            }
        }
    }

    /**
     * Report rage click event to analytics
     */
    private reportRageClick(
        clickId: string,
        text: string,
        elementType: string,
        clickCount: number
    ): void {
        const pageContext = pageTrackingService.getCurrentContext();

        trackEvent("Click.RageClick.Detected", {
            clickId,
            elementText: text,
            elementType,
            clickCount,
            currentPage: pageContext.currentPage || "unknown",
            sessionId: pageContext.sessionId,
            navigationDepth: pageContext.navigationDepth,
            trackingVersion: TRACKING_VERSION,
        });
    }

    /**
     * Find closest trackable element (button or link) in DOM tree
     */
    private findTrackableElement(element: HTMLElement): HTMLElement | null {
        let current: HTMLElement | null = element;

        // Traverse up to 4 levels to find trackable element
        for (let i = 0; i < 4 && current; i++) {
            if (TRACKABLE_ELEMENTS.includes(current.tagName)) {
                return current;
            }
            current = current.parentElement;
        }

        return null;
    }

    /**
     * Extract and sanitize element text content
     */
    private getElementText(element: HTMLElement): string {
        const text = element.textContent || element.getAttribute("aria-label") || "";
        return text.trim().substring(0, MAX_TEXT_LENGTH);
    }

    /**
     * Manually record a click event (for cases where data-track-click can't be used)
     * @param id - The tracking ID for the click
     * @param text - Optional text content to record (defaults to empty string)
     * @param elementType - Optional element type (defaults to "button")
     */
    recordClick(id: ClickId, text: string = "", elementType: string = "button"): void {
        const now = Date.now();

        // Detect rage clicks for manual clicks (without debouncing)
        this.detectRageClickUnbounced(id, text, elementType, now);

        const lastClickTime = this.lastClickTimes.get(id);

        // Apply same debouncing as normal clicks
        if (lastClickTime && now - lastClickTime < DEBOUNCE_MS) {
            return;
        }

        this.lastClickTimes.set(id, now);

        this.clicks.push({
            id,
            text: text.trim().substring(0, MAX_TEXT_LENGTH),
            elementType,
            timeFromPageStart: now - this.pageStartTime,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Get all clicks for current page and reset
     * Called by PageTrackingService when page exits
     */
    getClicksForPage(): ClickData {
        const data: ClickData = {
            clicks: this.clicks,
            clickCount: this.clicks.length,
        };

        // Reset for next page
        this.clicks = [];

        return data;
    }

    /**
     * Reset page start time when new page is viewed
     * Called by PageTrackingService when page view starts
     */
    resetPageStartTime(): void {
        this.pageStartTime = Date.now();
        this.clicks = [];
        this.lastClickTimes.clear();
        this.recentClicks = [];
        this.rageClicksDetected.clear();
        this.rageClickBuffer.clear();
    }

    /**
     * Initialize global click listener
     * Safe to call multiple times - will only add listener once
     */
    initialize(): void {
        // Prevent duplicate listeners
        if (this.isInitialized) {
            return;
        }

        // Create bound function once and store it
        this.boundHandleClick = this.handleClick.bind(this);
        document.addEventListener("click", this.boundHandleClick, true);
        this.isInitialized = true;
    }

    /**
     * Remove global click listener
     */
    destroy(): void {
        if (this.boundHandleClick) {
            document.removeEventListener("click", this.boundHandleClick, true);
            this.boundHandleClick = null;
            this.isInitialized = false;
        }
    }
}

// Singleton instance
export const clickTrackingService = new ClickTrackingService();
